import { I18nService } from '../../i18n/I18nService';
import { I18N_KEYS } from '../../i18n/keys';
import { AccessibilityManager } from '../../accessibility/AccessibilityManager';

export class NumberGrid {
  private i18n: I18nService;
  private a11y: AccessibilityManager;
  private element: HTMLElement;
  private cells: Map<number, HTMLElement> = new Map();

  constructor(i18n: I18nService, a11y: AccessibilityManager) {
    this.i18n = i18n;
    this.a11y = a11y;
    this.element = document.getElementById('number-grid') || document.createElement('div');
  }

  render(min: number, max: number, calledNumbers: number[], currentNumber: number | null): void {
    this.element.setAttribute('role', 'grid');
    this.element.setAttribute('aria-label', this.i18n.t(I18N_KEYS.A11Y_GRID_LABEL));
    this.element.className = 'grid';

    const calledSet = new Set(calledNumbers);
    let html = '';
    const cols = window.innerWidth < 480 ? 5 : 10;

    for (let num = min; num <= max; num++) {
      if ((num - min) % cols === 0) {
        if (num > min) html += '</div>';
        html += '<div class="grid__row" role="row">';
      }

      const isCalled = calledSet.has(num);
      const isCurrent = num === currentNumber;
      let cellClass = 'grid-cell';
      if (isCurrent) {
        cellClass += ' grid-cell--current';
      } else if (isCalled) {
        cellClass += ' grid-cell--active';
      } else {
        cellClass += ' grid-cell--inactive';
      }

      const ariaLabel = isCalled
        ? this.i18n.t(I18N_KEYS.A11Y_CELL_CALLED, { number: num })
        : this.i18n.t(I18N_KEYS.A11Y_CELL_NOT_CALLED, { number: num });

      html += `<div class="${cellClass}" role="gridcell" aria-label="${ariaLabel}" data-number="${num}">${num}</div>`;
    }
    html += '</div>';

    this.element.innerHTML = html;

    // Cache cell references
    this.cells.clear();
    this.element.querySelectorAll('.grid-cell').forEach((cell) => {
      const num = parseInt(cell.getAttribute('data-number') || '0', 10);
      this.cells.set(num, cell as HTMLElement);
    });
  }

  highlightCell(number: number): void {
    // Remove previous "current" highlight
    const prevCurrent = this.element.querySelector('.grid-cell--current');
    if (prevCurrent) {
      prevCurrent.classList.remove('grid-cell--current');
      prevCurrent.classList.add('grid-cell--active');
    }

    const cell = this.cells.get(number);
    if (cell) {
      cell.classList.remove('grid-cell--inactive');
      cell.classList.add('grid-cell--current');
      this.a11y.updateGridCellLabel(cell, number, true);
    }
  }

  markSilent(number: number): void {
    const cell = this.cells.get(number);
    if (cell) {
      cell.classList.add('grid-cell--silent');
      cell.setAttribute('title', '🔇');
    }
  }

  resetGrid(): void {
    this.cells.forEach((cell) => {
      cell.classList.remove('grid-cell--active', 'grid-cell--current', 'grid-cell--silent');
      cell.classList.add('grid-cell--inactive');
      const num = parseInt(cell.getAttribute('data-number') || '0', 10);
      this.a11y.updateGridCellLabel(cell, num, false);
    });
  }
}
